import { Component, OnInit, signal, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';

import { UserLoginRole } from 'src/app/enumeration/roles';
import { QrApiService } from 'src/app/service/qr/qr-api.service';
import { CheckRolesAccessService } from 'src/app/utils/check_roles_access/check-roles-access.service';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';

@Component({
  selector: 'app-qr-code-details',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DateFormatPipe,
  ],
  templateUrl: './qr-code-details.component.html',
  styleUrls: ['./qr-code-details.component.css'],
})
export class QrCodeDetailsComponent implements OnInit {
  // ─── Modern Services Functional Injection Engine ───
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(QrApiService);
  private readonly checkAccessRoles = inject(CheckRolesAccessService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── Fine-Grained Reactive Signals Architecture ───
  readonly loading = signal<boolean>(false);
  readonly operationLoading = signal<boolean>(false);
  readonly qrcodeitem = signal<any>(null);
  readonly isUpdateEnableDisable = signal<boolean>(false);

  // ─── Strict Component Properties ───
  serial: string = '';

  ngOnInit(): void {
    const routeParams = this.route.snapshot.paramMap;
    this.serial = String(routeParams.get('qrcode') || '');

    // Set role authorization state cleanly using a modern signal update routine
    this.isUpdateEnableDisable.set(
      this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN])
    );

    this.getQrcodeInfo();
  }

  /**
   * Retrieves full ledger parameter specs for a singular QR token reference
   */
  getQrcodeInfo(): void {
    if (!this.serial) return;
    
    this.loading.set(true);
    this.apiService.getQrcodeSingleService(this.serial)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (v) => this.qrcodeitem.set(v || null),
        error: (e) => {
          if (e) alert(e);
        }
      });
  }

  /**
   * Triggers a synchronous prompt to alter data credit point thresholds
   */
  updatepoints(): void {
    const inputvalue = prompt('Enter point value');
    if (!inputvalue?.trim()) return;

    if (confirm(`Confirm: ${inputvalue}`)) {
      this.operationLoading.set(true);
      
      this.apiService
        .getUpdateQrcodePointService(this.serial, String(inputvalue.trim()))
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.operationLoading.set(false))
        )
        .subscribe({
          next: () => {
            alert('Success');
            this.getQrcodeInfo();
          },
          error: (e) => {
            if (e) alert(e);
          }
        });
    }
  }

  /**
   * Toggles asset validation state access rules globally
   */
  enableQrcode(type: string): void {
    if (!confirm(type)) return;

    this.operationLoading.set(true);
    this.apiService.getEnableQrcode(type, this.serial)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.operationLoading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Success');
          this.getQrcodeInfo();
        },
        error: (e) => {
          if (e) alert(e);
        }
      });
  }
}
